/* Rúngne Climber Quiz v2 — "It's not a form. It's a session."
 * Framework-free custom element. No cookies, no storage, no third-party requests.
 * Scoring is transparent creative routing (climbing-routing-draft-v1), not a validated model.
 * Network: only the two optional endpoints in config.endpoints (check-in, result). Absent = local only.
 */
(() => {
  'use strict';
  if (customElements.get('rungne-climber-quiz')) return;

  const esc = v => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const safeURL = v => { try { const u = new URL(v, location.href); return ['http:', 'https:', 'file:'].includes(u.protocol) ? u.href : '#'; } catch (_) { return '#'; } };
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const titleCase = v => String(v || '').toLowerCase().replace(/(^|\s)(\S)/g, (m, sp, c) => sp + c.toUpperCase());
  const sentenceCase = v => String(v || '').toLowerCase().replace(/(^\s*|[.!?]\s+)([a-z\u00e0-\u00ff])/g, (m, pre, c) => pre + c.toUpperCase());

  function calculate(data, answers) {
    const totals = { analysis: 0, approach: 0, reliability: 0, spontaneity: 0 };
    let complete = 0;
    for (const q of data.questions) {
      const a = q.answers.find(x => x.id === answers[q.id]);
      if (!a) continue;
      complete++;
      for (const k of Object.keys(totals)) totals[k] += Number(a.weights[k] || 0);
    }
    const motive = totals.approach - totals.reliability;
    const detailed = totals.analysis >= 0, possibility = motive >= 0;
    const id = detailed ? (possibility ? 'beta-scientist' : 'safety-officer') : (possibility ? 'psycho' : 'local');
    return { id, complete, totals, motive, mixed: Math.abs(totals.analysis) <= 2 || Math.abs(motive) <= 2,
      style: Math.abs(totals.spontaneity) <= 2 ? 'a mix of planning and spontaneity' : totals.spontaneity > 0 ? 'a more spontaneous session style' : 'a more planned session style' };
  }
  window.RungneQuizScoring = { calculate };

  // The topo: twelve holds on one route. done = answered set, cur = current index.
  function topo(orient, w, h, n, doneSet, cur, light, cls = '') {
    const pts = [];
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      pts.push(orient === 'v' ? [w * (0.35 + 0.3 * Math.sin(i * 1.9)), h - 14 - t * (h - 28)] : [14 + t * (w - 28), h * (0.5 + 0.28 * Math.sin(i * 1.9))]);
    }
    const line = light ? '#F1E5C8' : '#10191A';
    let s = `<svg class="${cls}" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true"><path d="M${pts.map(p => p.map(v => v.toFixed(1)).join(' ')).join(' L')}" fill="none" stroke="${line}" stroke-opacity=".55" stroke-width="1.5"/>`;
    pts.forEach(([x, y], i) => {
      if (i === cur) s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="9" fill="#E6532E"/>`;
      else if (doneSet.has(i)) s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="6" fill="${line}"/>`;
      else s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="6" fill="none" stroke="${line}" stroke-width="1.5"/>`;
    });
    return s + '</svg>';
  }

  class RungneQuiz extends HTMLElement {
    connectedCallback() { if (!this._ready) queueMicrotask(() => { if (this.isConnected && !this._ready) this.init(); }); }
    disconnectedCallback() { this.events?.abort(); clearTimeout(this.timer); if (this.dialog?.open) this.close(); this._ready = false; }

    init() {
      try {
        this.config = JSON.parse(this.querySelector('[data-rq-config]')?.textContent || '{}');
        this.data = window.RQ_QUIZ_DATA;
        if (!this.data || this.data.questions.length !== 12) throw new Error('Quiz data is missing.');
        this._ready = true;
        this.events = new AbortController();
        this.uid = 'rq-' + (this.id || Math.random().toString(36).slice(2, 9));
        this.reset();
        const mount = this.querySelector('[data-rq-mount]') || this.appendChild(document.createElement('div'));
        mount.innerHTML = this.sectionHTML() + `<dialog class="rq-dialog" aria-label="Rúngne climber quiz"><div class="rq-frame">
          <header class="rq-bar"><div class="rq-bar-left"><img class="rq-logo" src="${esc(this.asset('rq-logo-icecap.svg'))}" alt="Rúngne" width="76" height="28"><p class="rq-k" data-rq-kicker>The Rúngne climber quiz</p></div>
          <div class="rq-bar-right"><p class="rq-k" data-rq-step></p><button type="button" class="rq-close" data-action="close" aria-label="Close quiz">×</button></div></header>
          <div class="rq-topo-h"><p class="rq-k">Start</p><div data-rq-topo-h></div><p class="rq-k rq-topo-top">Top ${this.img('rq-tote.webp', 'Free tote at the top', 'class="rq-topo-tote"')}</p></div>
          <div class="rq-body"><aside class="rq-rail" aria-hidden="true"><p class="rq-k">Top</p><div data-rq-topo-v></div><p class="rq-k">Start</p></aside>
          <section class="rq-sheet"><div class="rq-ink" data-rq-stage></div><div class="rq-toast" data-rq-toast role="status" aria-live="polite"></div></section></div></div></dialog>`;
        this.dialog = this.querySelector('dialog'); this.sheet = this.querySelector('.rq-sheet'); this.stage = this.querySelector('[data-rq-stage]');
        const sig = { signal: this.events.signal };
        this.addEventListener('click', e => this.onClick(e), sig);
        this.addEventListener('change', e => this.onChange(e), sig);
        this.addEventListener('submit', e => this.onSubmit(e), sig);
        this.addEventListener('error', e => { if (e.target.tagName === 'IMG') this.imgFallback(e.target); }, { capture: true, ...sig });
        // Escape closes the share panel first, the quiz only after that.
        this.dialog.addEventListener('cancel', e => { e.preventDefault(); if (!this.closeSharePanel(true)) this.close(); }, sig);
        this.dialog.addEventListener('keydown', e => this.onKey(e), sig);
        const p = new URLSearchParams(location.search);
        if (!this.config.ignoreQuery) {
          const type = this.profile(p.get('type'));
          if (p.get('climber-quiz') === '1' && type) { this.result = { id: type.id, shared: true }; this.figure = this.figureIds().includes(p.get('figure')) ? p.get('figure') : null; this.open('result'); }
          else if (p.get('climber-quiz') === '1') this.open('checkin');
          else if (this.config.demo && p.get('rq-screen')) this.demoJump(p.get('rq-screen'));
        }
        this.dispatchEvent(new CustomEvent('rq:ready'));
      } catch (err) {
        this.innerHTML = `<div style="padding:24px"><b>That didn’t stick.</b> The quiz could not load. ${esc(err.message)}</div>`;
        console.error('Rúngne quiz:', err);
      }
    }
    reset() { this.answers = {}; this.position = 0; this.result = null; this.figure = null; this.randomFigure = null; this.checkin = null; this.resultKey = null; this.requestKey = null; this.tote = null; this.screen = 'section'; }

    /* ---------- helpers ---------- */
    asset(name) { return this.config.assets?.[name] || (this.config.assetBase || 'assets/') + name; }
    img(name, alt = '', extra = '', fallback = '') {
      const fb = fallback && fallback !== name ? ` data-fallback="${esc(this.asset(fallback))}"` : '';
      return `<img src="${esc(this.asset(name))}"${fb} alt="${esc(alt)}" decoding="async" ${extra}>`;
    }
    imgFallback(el) {
      if (el.dataset.fallback && !el.dataset.fellBack) { el.dataset.fellBack = '1'; el.src = el.dataset.fallback; return; }
      el.style.visibility = 'hidden'; el.dataset.failed = '1';
    }
    profile(id) { return this.data.profiles.find(p => p.id === id); }
    activeProfile() { return this.profile(this.result?.id) || this.data.profiles[0]; }
    figureIds() { return (this.data.figure?.options || []).map(o => o.id).filter(id => id !== 'skip'); }
    activeFigure() {
      if (this.figureIds().includes(this.figure)) return this.figure;
      const ids = this.figureIds(); if (!ids.length) return null;
      if (!this.randomFigure) this.randomFigure = ids[Math.floor(Math.random() * ids.length)];
      return this.randomFigure;
    }
    profileImage(p) { return p.images?.[this.activeFigure()] || p.image; }
    btn(label, action, primary = true, extra = '') { return `<button type="button" class="rq-btn ${primary ? '' : 'rq-ghost'}" data-action="${action}" ${extra}>${label}</button>`; }
    toast(text) { const n = this.querySelector('[data-rq-toast]'); if (!n) return; n.textContent = text; n.classList.add('rq-on'); clearTimeout(this.toastTimer); this.toastTimer = setTimeout(() => n.classList.remove('rq-on'), 2600); }
    shareLink() { const u = new URL(this.config.quizUrl || location.href); u.search = ''; u.searchParams.set('climber-quiz', '1'); u.searchParams.set('type', this.activeProfile().id); const f = this.activeFigure(); if (f) u.searchParams.set('figure', f); return u.href; }
    /* ---------- the share card ---------- */

    /** Load one image for the canvas. Same-origin theme assets, so untainted. */
    loadImage(src) {
      return new Promise((res, rej) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => res(im);
        im.onerror = () => rej(new Error('image ' + src));
        im.src = src;
      });
    }

    /** A canvas never asks for a web font on its own — load the two it draws with. */
    async cardFonts() {
      try { await Promise.all(['600 78px "Sequel Sans"', '400 30px "Sequel Sans"'].map(f => document.fonts.load(f))); }
      catch { /* the fallback stack still draws a card */ }
    }

    /** Set the largest font (max → min) at which the text fits the width on one line. The band has no room for a wrap. */
    fitFont(ctx, weight, max, min, text, width) {
      for (let s = max; s > min; s -= 2) {
        ctx.font = `${weight} ${s}px "Sequel Sans", Helvetica, Arial, sans-serif`;
        if (ctx.measureText(text).width <= width) return;
      }
      ctx.font = `${weight} ${min}px "Sequel Sans", Helvetica, Arial, sans-serif`;
    }

    /** The screenprint grain every sheet carries, drawn (not loaded) so the canvas stays exportable. */
    grain(ctx, x, y, w, h) {
      const t = document.createElement('canvas'); t.width = t.height = 160;
      const g = t.getContext('2d'); if (!g) return;
      const d = g.createImageData(160, 160);
      for (let i = 0; i < d.data.length; i += 4) { d.data[i] = 16; d.data[i + 1] = 25; d.data[i + 2] = 26; d.data[i + 3] = Math.random() < 0.5 ? Math.round(Math.random() * 22) : 0; }
      g.putImageData(d, 0, 0);
      ctx.save(); ctx.fillStyle = ctx.createPattern(t, 'repeat'); ctx.fillRect(x, y, w, h); ctx.restore();
    }

    /**
     * The poster every finisher gets: 1080x1920, a phone story. The whole
     * illustration on top at its own 2:3 — never cropped, because a cropped
     * card cut off the Psycho's hand and the Safety Officer's head — and a
     * printed paper band under it with the kicker, the type, the line,
     * rungne.com and the mark. The figure is the one the person chose, so the
     * thing they keep is the thing they saw.
     *
     * The mark is the official Midnight asset on the paper (brand rule: Midnight
     * on light, never recoloured, never typeset). Returns null when no card can
     * be made (the art would not load, the canvas cannot be exported) — the
     * result screen then keeps the plain illustration and sharing falls back to
     * the link.
     */
    async shareCard() {
      const p = this.activeProfile();
      const W = 1080, ART = 1620, H = 1920;
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      const css = getComputedStyle(this);
      const v = (n, fb) => (css.getPropertyValue(n) || '').trim() || fb;
      const PAPER = v('--rq-paper', '#F1E5C8'), INK = v('--rq-ink', '#10191A'), MUTE = v('--rq-mute', '#5F5E57');
      const FONT = '"Sequel Sans", Helvetica, Arial, sans-serif';
      await this.cardFonts();

      // the art, whole — the chosen figure, else the type's default image
      let art = null;
      for (const name of new Set([this.profileImage(p), p.image].filter(Boolean))) {
        try { art = await this.loadImage(this.asset(name)); break; } catch { /* try the next */ }
      }
      if (!art) return null;
      const r = Math.max(W / art.width, ART / art.height);
      const aw = art.width * r, ah = art.height * r;
      ctx.save(); ctx.beginPath(); ctx.rect(0, 0, W, ART); ctx.clip();
      ctx.drawImage(art, (W - aw) / 2, (ART - ah) / 2, aw, ah);
      ctx.restore();

      // the paper band, printed like every sheet
      ctx.fillStyle = PAPER; ctx.fillRect(0, ART, W, H - ART);
      this.grain(ctx, 0, ART, W, H - ART);
      ctx.fillStyle = INK; ctx.fillRect(0, ART, W, 5);

      const PAD = 64, TEXT_W = W - PAD * 2;
      ctx.textBaseline = 'alphabetic'; ctx.textAlign = 'left';
      ctx.fillStyle = INK;
      ctx.font = `600 22px ${FONT}`;
      ctx.letterSpacing = '3px';
      ctx.fillText('WHAT KIND OF CLIMBER ARE YOU?  ·  ' + p.number + ' OF 04', PAD, ART + 70);
      ctx.letterSpacing = '0px';

      // The mark, opposite the kicker — a card reshared with no caption still says who made it.
      try {
        const logo = await this.loadImage(this.asset('rq-logo-midnight.svg'));
        const lh = 46, lw = lh * (270 / 100);
        ctx.drawImage(logo, W - PAD - lw, ART + 40, lw, lh);
      } catch { /* no mark is better than a broken one */ }

      this.fitFont(ctx, 600, 78, 48, p.name, TEXT_W);
      ctx.fillText(p.name, PAD, ART + 160);

      ctx.fillStyle = MUTE;
      this.fitFont(ctx, 400, 30, 20, p.line, TEXT_W);
      ctx.fillText(p.line, PAD, ART + 212);

      ctx.fillStyle = INK;
      ctx.font = `600 24px ${FONT}`;
      ctx.fillText('rungne.com', PAD, H - 40);

      try {
        const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
        if (!blob) return null;
        return new File([blob], `rungne-${p.id}.png`, { type: 'image/png' });
      } catch { return null; }   // tainted canvas — the link still works
    }

    /* ---------- the poster on the result screen ---------- */

    cardKey() { return `${this.activeProfile().id}:${this.activeFigure() || ''}`; }

    /**
     * The finisher's card, drawn once per type + figure and kept, so a repaint
     * (the tote code arriving) or a second tap on Share never draws it twice.
     */
    async ensureCard() {
      const key = this.cardKey();
      if (this.card?.key === key) return this.card.file;
      if (this.cardJob?.key !== key) this.cardJob = { key, promise: this.shareCard().catch(() => null) };
      const job = this.cardJob;
      const file = await job.promise;
      if (this.cardJob === job) this.cardJob = null;
      if (!file) return null;
      if (this.card?.key !== key) {
        if (this.card?.url) URL.revokeObjectURL(this.card.url);
        this.card = { key, file, url: URL.createObjectURL(file) };
      }
      return this.card.file;
    }

    posterImg(p) {
      return `<img src="${esc(this.card.url)}" alt="${esc(`Your Rúngne poster: ${titleCase(p.name)}. ${sentenceCase(p.line)}`)}" width="1080" height="1920" decoding="async">`;
    }

    /** Every finisher gets the card: drawn as soon as the result lands, swapped into the poster slot. */
    async paintCard() {
      const key = this.cardKey();
      const file = await this.ensureCard();
      if (this.screen !== 'result' || this.cardKey() !== key) return;
      const slot = this.stage.querySelector('[data-rq-poster]');
      if (!slot || slot.classList.contains('rq-poster-card')) return;
      if (!file) { slot.classList.add('rq-poster-plain'); return; }   // the illustration stays; sharing falls back to the link
      slot.classList.add('rq-poster-card');
      slot.innerHTML = this.posterImg(this.activeProfile());
    }

    /** Save the card itself — a download on a computer, the Files sheet on a phone. */
    async save() {
      const file = await this.ensureCard();
      if (!file || !this.card) { this.toast('Could not make the poster here. Share the link instead.'); return; }
      const a = document.createElement('a');
      a.href = this.card.url; a.download = file.name;
      document.body.appendChild(a); a.click(); a.remove();
      this.toast('Poster saved.');
    }

    /**
     * Hand the type on. A phone opens its own share sheet with the card attached
     * (Instagram, Messages, Save Image…). A computer has no such sheet, so the
     * button opens a small panel right where it was clicked: the link copied,
     * and one-tap routes. Nothing is awaited before navigator.share — Safari
     * drops the tap's permission across an await and the share fails silently.
     * Whoever opens the link sees the same poster under "a climbing type,
     * shared with you" — the view the URL parser already renders.
     */
    share() {
      const url = this.shareLink();
      const p = this.activeProfile();
      const text = `I came out as ${titleCase(p.name)}. ${sentenceCase(p.line)}`;
      const title = 'What kind of climber are you?';
      // The native sheet only on a touch device, where it is the norm and reliably answers. Desktop
      // Chrome on macOS exposes navigator.share too, but its promise can hang with no sheet at all —
      // the "nothing happens when I click it" of 2026-09-21 — so a computer always gets the panel.
      const touch = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
      if (touch && typeof navigator.share === 'function') {
        // The card is drawn when the result lands; if it is somehow not ready, share the link alone.
        const file = this.card?.key === this.cardKey() ? this.card.file : null;
        let data = { title, text, url };
        try { if (file && navigator.canShare?.({ files: [file] })) data = { title, text, url, files: [file] }; } catch { /* link only */ }
        let shared;
        try { shared = navigator.share(data); } catch (err) { shared = Promise.reject(err); }
        shared.catch(err => { if (err?.name !== 'AbortError') this.openSharePanel(url, text); });   // closed the sheet = done
        return;
      }
      this.openSharePanel(url, text);
    }

    /** The computer's share: a panel over the buttons with the link (copied straight away) and the usual routes. */
    openSharePanel(url, text) {
      const host = this.stage.querySelector('[data-rq-share-panel]');
      if (!host) return;
      const enc = encodeURIComponent;
      const routes = [
        ['WhatsApp', `https://wa.me/?text=${enc(`${text} ${url}`)}`],
        ['Facebook', `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`],
        ['X', `https://x.com/intent/post?text=${enc(text)}&url=${enc(url)}`],
        ['Email', `mailto:?subject=${enc('What kind of climber are you?')}&body=${enc(`${text}\n\n${url}`)}`],
      ];
      host.innerHTML = `<div class="rq-share-head"><p class="rq-k">Share the poster</p><button type="button" class="rq-share-x" data-action="share-close" aria-label="Close">×</button></div>
        <p class="rq-share-status" data-rq-share-status role="status" aria-live="polite">Copying the link…</p>
        <div class="rq-share-link"><input class="rq-input" type="text" readonly value="${esc(url)}" aria-label="Link to your result" data-rq-share-url><button type="button" class="rq-btn" data-action="copy-link">Copy</button></div>
        <div class="rq-share-routes">${routes.map(([n, h]) => `<a class="rq-btn rq-ghost" href="${esc(h)}" target="_blank" rel="noopener">${n}</a>`).join('')}</div>
        <p class="rq-fine">For Instagram: save the poster and post it from your phone.</p>`;
      host.hidden = false;
      this.stage.querySelector('[data-action="share"]')?.setAttribute('aria-expanded', 'true');
      this.copyLink(url);
    }
    closeSharePanel(restoreFocus = false) {
      const host = this.stage?.querySelector('[data-rq-share-panel]');
      if (!host || host.hidden) return false;
      host.hidden = true; host.innerHTML = '';
      const btn = this.stage.querySelector('[data-action="share"]');
      btn?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) btn?.focus({ preventScroll: true });
      return true;
    }
    /** Copy the result link. When the clipboard refuses, the link is selected in its field so ⌘C / Ctrl+C still works. */
    copyLink(url = this.shareLink()) {
      const status = this.stage.querySelector('[data-rq-share-status]');
      const field = this.stage.querySelector('[data-rq-share-url]');
      const done = ok => {
        if (status) status.textContent = ok ? 'Link copied. Paste it anywhere.' : 'Copy the link below (⌘C / Ctrl+C).';
        if (!ok && field) { field.focus(); field.select(); }
      };
      try { navigator.clipboard.writeText(url).then(() => done(true), () => done(false)); } catch { done(false); }
    }
    post(kind, body) {
      const url = this.config.endpoints?.[kind]; if (!url) return Promise.resolve(null);
      return fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), keepalive: true, credentials: 'omit' })
        .then(r => r.ok ? r.json().catch(() => ({})) : null).catch(() => null);
    }

    /* ---------- the tote (completion reward; frontend only until Atlas issues the code) ---------- */
    toteTag() {
      return `<div class="rq-tote-tag">${this.img('rq-tote.webp', 'Rúngne canvas tote', 'loading="lazy"')}<p><b>Top out, take the tote.</b><span>Finish the quiz and get a free Rúngne tote with your next order.</span></p></div>`;
    }
    toteReward() {
      const t = this.tote;
      const line = t?.code
        ? `<b>A free tote with your next order.</b><span class="rq-tote-code">${esc(t.code)}</span><span>One per climber. It works once${t.expires_on ? `, and expires on ${esc(t.expires_on)}` : ''} — it is also in your inbox.</span>`
        : `<b>A free tote with your next order.</b><span>It rides along with your result email. One per climber.</span>`;
      return `<div class="rq-tote-reward"><div class="rq-tote-reward-art">${this.img('rq-tote.webp', 'Rúngne canvas tote', 'loading="eager"')}</div><p><span class="rq-k">You earned it</span>${line}</p></div>`;
    }

    /* ---------- section on the page (E01) ---------- */
    sectionHTML() {
      const h = esc(this.config.heading || 'What kind of climber are you?');
      return `<div class="rq-shell"><section class="rq-section" style="--rq-hero:url('${esc(this.asset(this.data.assets?.hero || 'rq-hero-psycho.webp'))}');--rq-teal-tile:url('${esc(this.asset('rq-hero-teal.webp'))}')"><div class="rq-section-copy"><p class="rq-k">The Rúngne climber quiz</p><h2>${h}</h2><p class="rq-section-sub">12 questions. Same obsession. Different minds.</p>
        ${this.btn(esc(this.config.cta || 'Check in') + ' →', 'open')}<p class="rq-section-note">Twelve problems. Four kinds of climber. No grade required.</p>${this.toteTag()}</div>
        <div class="rq-section-art">${this.img(this.data.assets?.hero || 'rq-hero-psycho.webp', 'The Psycho mid-dyno on a bouldering wall', 'loading="eager"')}</div></section></div>`;
    }

    /* ---------- dialog ---------- */
    open(screen) {
      this.returnEl = document.activeElement;
      if (!this.dialog.open) { this.oldOverflow = document.documentElement.style.overflow; document.documentElement.style.overflow = 'hidden'; this.dialog.showModal(); }
      if (screen === 'checkin' && this.checkin) screen = this.result ? 'result' : 'problem';
      this.render(screen);
    }
    close() {
      clearTimeout(this.timer);
      if (this.dialog.open) this.dialog.close();
      document.documentElement.style.overflow = this.oldOverflow || '';
      if (this.returnEl?.isConnected) this.returnEl.focus({ preventScroll: true });
    }
    render(screen) {
      clearTimeout(this.timer);
      this.screen = screen;
      const done = new Set(this.data.questions.map((q, i) => this.answers[q.id] ? i : -1).filter(i => i >= 0));
      const cur = screen === 'problem' ? this.position : (screen === 'topout' || screen === 'result' ? -1 : -2);
      const showTopo = ['checkin', 'problem', 'topout', 'result'].includes(screen); // the empty route is visible from check-in
      this.querySelector('[data-rq-topo-v]').innerHTML = showTopo ? topo('v', 88, 480, 12, done, cur, true) : '';
      this.querySelector('[data-rq-topo-h]').innerHTML = showTopo ? topo('h', 1000, 40, 12, done, cur, false, 'rq-topo-wide') + topo('h', 300, 40, 12, done, cur, false, 'rq-topo-narrow') : '';
      this.querySelector('.rq-topo-h').style.display = showTopo ? '' : 'none';
      const step = { problem: `Problem ${String(this.position + 1).padStart(2, '0')} of 12`, checkin: 'Check-in', topout: 'Topping out', result: '' }[screen] || '';
      this.querySelector('[data-rq-step]').textContent = step;
      this.querySelector('[data-rq-kicker]').textContent = screen === 'result' ? 'You topped out' : 'The Rúngne climber quiz';
      const html = { checkin: () => this.checkinHTML(), problem: () => this.problemHTML(), topout: () => this.topoutHTML(), result: () => this.resultHTML() }[screen];
      this.stage.innerHTML = html ? html() : '';
      this.sheet.scrollTop = 0;
      this.sheet.classList.remove('rq-print', 'rq-leave'); void this.sheet.offsetWidth; this.sheet.classList.add('rq-print');
      const h = this.stage.querySelector('[data-rq-heading]'); if (h) setTimeout(() => h.focus({ preventScroll: true }), 60);
      if (screen === 'topout') this.timer = setTimeout(() => this.render('result'), 2100);
      if (screen === 'result') this.paintCard();
    }
    leaveThen(fn) {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return fn();
      this.sheet.classList.add('rq-leave'); setTimeout(fn, 140);
    }

    /* ---------- check-in ---------- */
    checkinHTML() {
      const f = this.data.figure;
      const chips = f ? `<div class="rq-field"><p class="rq-k">${esc(f.question)}</p><div class="rq-chips">${f.options.map(o => `<label class="rq-chip"><input type="radio" name="${this.uid}-figure" value="${esc(o.id)}" ${this.figure === o.id ? 'checked' : ''}><span>${esc(o.label)}</span></label>`).join('')}</div><p class="rq-fine">${esc(f.note)}</p></div>` : '';
      return `<form class="rq-checkin" data-rq-checkin novalidate><div class="rq-checkin-form">
        <p class="rq-k" style="color:var(--rq-rust)">Check-in</p>
        <h1 data-rq-heading tabindex="-1">Check in.<br>Then climb.</h1>
        <p class="rq-lead">Drop your email, pick your climber, and climb twelve problems. Top out and your type lands in your inbox – with a free Rúngne tote on your next order.</p>
        <div class="rq-field"><label class="rq-k" for="${this.uid}-email">Email</label><input class="rq-input" id="${this.uid}-email" name="email" type="email" inputmode="email" autocomplete="email" placeholder="you@somewhere.com" required maxlength="254" value="${esc(this.checkin?.email || '')}"><p class="rq-error" data-rq-error aria-live="polite"></p></div>
        ${chips}
        <label class="rq-check"><input type="checkbox" name="marketing" required ${this.checkin?.marketing ? 'checked' : ''}><span>Send me Rúngne emails that fit my type. Unsubscribe any time.</span></label><p class="rq-error" data-rq-error-consent aria-live="polite"></p>
        <div><button type="submit" class="rq-btn" style="width:100%">Check in →</button></div>
        <p class="rq-fine">We keep your answers anonymous. Your email gets your result and emails that match your type. <a href="${esc(safeURL(this.config.privacyUrl || '/policies/privacy-policy'))}" target="_blank" rel="noopener">Privacy</a>.</p>
      </div><div class="rq-checkin-art rq-block">${this.img(this.data.assets?.hero || 'rq-hero-psycho.webp', '', 'loading="eager"')}</div></form>`;
    }
    onSubmit(e) {
      const form = e.target.closest('[data-rq-checkin]'); if (!form) return;
      e.preventDefault();
      const input = form.querySelector('[name="email"]'), err = form.querySelector('[data-rq-error]');
      const email = (input.value || '').trim();
      if (!EMAIL.test(email)) { err.textContent = 'That email doesn’t look right. One more go.'; input.setAttribute('aria-invalid', 'true'); input.focus(); return; }
      err.textContent = ''; input.removeAttribute('aria-invalid');
      const consent = form.querySelector('[name="marketing"]'), cerr = form.querySelector('[data-rq-error-consent]');
      if (consent && !consent.checked) { cerr.textContent = 'Tick the box to check in – the quiz runs on email.'; consent.focus(); return; }
      if (cerr) cerr.textContent = '';
      this.checkin = { email, marketing: true, figure: this.figure || 'skip', at: new Date().toISOString() };
      this.post('checkin', { ...this.checkin, source: this.config.source || 'site' }).then(r => { if (r?.checkin_key) this.checkin.key = r.checkin_key; });
      this.position = 0;
      this.leaveThen(() => this.render('problem'));
    }

    /* ---------- problem ---------- */
    problemHTML() {
      const q = this.data.questions[this.position], chosen = this.answers[q.id];
      return `<div class="rq-problem"><div class="rq-meta"><p class="rq-k">Problem ${String(this.position + 1).padStart(2, '0')}</p><p class="rq-k">${esc(q.chapter || '')}</p></div>
        <h1 data-rq-heading tabindex="-1">${esc(q.title)}</h1>
        <div class="rq-cards" role="radiogroup" aria-label="Answers">${q.answers.map(a => `<label class="rq-card rq-block"><input type="radio" name="${this.uid}-${q.id}" value="${esc(a.id)}" data-rq-answer ${chosen === a.id ? 'checked' : ''}>${this.img(a.asset, '', 'loading="eager"')}<i aria-hidden="true">${a.id.toUpperCase()}</i><span>${esc(a.label)}</span></label>`).join('')}</div>
        <div class="rq-foot">${this.btn('← Back', 'back', false)}<span class="rq-hint">A–D to pick · Enter for next</span>${this.btn(this.position === 11 ? 'Top out →' : 'Next problem →', 'next', true, chosen ? '' : 'disabled')}</div></div>`;
    }
    onChange(e) {
      if (e.target.matches('[data-rq-figure], input[name$="-figure"]')) { this.figure = e.target.value; return; }
      if (e.target.matches('[data-rq-answer]')) {
        this.answers[this.data.questions[this.position].id] = e.target.value;
        const next = this.stage.querySelector('[data-action="next"]'); if (next) next.disabled = false;
        const doneNow = new Set(this.data.questions.map((q, i) => this.answers[q.id] ? i : -1).filter(i => i >= 0));
        this.querySelector('[data-rq-topo-h]').innerHTML = topo('h', 1000, 40, 12, doneNow, this.position, false, 'rq-topo-wide') + topo('h', 300, 40, 12, doneNow, this.position, false, 'rq-topo-narrow');
      }
    }
    next() {
      const q = this.data.questions[this.position]; if (!this.answers[q.id]) return;
      if (this.position < 11) { this.position++; this.leaveThen(() => this.render('problem')); }
      else this.finish();
    }
    finish() {
      this.result = calculate(this.data, this.answers);
      // One completion, one request key. Minted here and reused on a retry so a
      // flaky network cannot mint a second tote code that nobody can revoke.
      this.requestKey = this.requestKey || (crypto.randomUUID ? crypto.randomUUID() : null);
      this.post('result', {
        answers: this.answers, model_version: this.data.modelVersion, profile_id: this.result.id,
        mixed: this.result.mixed, figure: this.figure || 'skip', checkin_key: this.checkin?.key || null,
        source: this.config.source || 'site',
        // The server scores the sheet again and ignores profile_id; these two are
        // what it needs to mint and address the tote.
        email: this.checkin?.email || '', request_key: this.requestKey,
        profile_name: this.activeProfile()?.name || this.result.id,
        marketing: !!this.checkin?.marketing,
        locale: document.documentElement.lang || '', market: (window.Shopify && window.Shopify.country) || ''
      }).then(r => {
        if (r?.result_key) this.resultKey = r.result_key;
        if (r?.tote?.code) {
          this.tote = r.tote;
          // The result may already be on screen — repaint so the code appears
          // without the climber having to do anything.
          if (this.screen === 'result') this.render('result');
        }
      });
      this.leaveThen(() => this.render('topout'));
    }

    /* ---------- topping out ---------- */
    topoutHTML() {
      return `<div class="rq-topout"><div class="rq-topout-art rq-block">${this.img(this.data.assets?.transition || 'rq-transition.webp', '')}</div>
        <div class="rq-topout-copy"><p class="rq-k">Topping out</p><h1 data-rq-heading tabindex="-1">Reading the beta.</h1><p class="rq-lead">Twelve problems, one route. Pulling your sheet…</p><div class="rq-pull" aria-hidden="true"></div></div></div>`;
    }

    /* ---------- result ---------- */
    resultHTML() {
      const p = this.activeProfile(), shared = !!this.result?.shared, mixed = !shared && this.result?.mixed;
      const signal = shared ? '' : `<div class="rq-signal"><b>${mixed ? 'A bit of a crossover. That’s allowed.' : 'Your answers have a theme.'}</b>${mixed ? 'You sit close to the middle on one dimension. Treat this as the nearest type, not a fixed label.' : `This is your closest climbing type. Your answers also suggest ${esc(this.result.style)}.`}</div>`;
      const facts = [['Habitat', p.habitat], ['Usually says', p.phrase], ['Superpower', p.strength], ['Weak spot', p.weakness]];
      const gearUrl = safeURL(p.id === 'psycho' || p.id === 'local' ? (this.config.chalkUrl || '/collections/climbing-chalk') : (this.config.gearUrl || '/collections/climbing-gear'));
      return `<div class="rq-result" style="--rq-accent:${esc(p.accent)}"><div class="rq-result-copy">
        <p class="rq-k">${shared ? 'A climbing type, shared with you' : 'Your type'} · ${esc(p.number)} of 04</p>
        <h1 data-rq-heading tabindex="-1">${esc(titleCase(p.name))}</h1>
        <p class="rq-result-line">${esc(sentenceCase(p.line))}</p>
        ${shared ? '' : this.toteReward()}
        <p class="rq-lead">${esc(p.description)}</p>
        <dl class="rq-facts">${facts.map(([k, v]) => `<div><dt>${k}</dt><dd>${esc(v)}</dd></div>`).join('')}</dl>
        ${signal}
        <div class="rq-actions">${shared ? this.btn('Find my own type →', 'restart') : ''}<a class="rq-btn rq-ghost" href="${gearUrl}">${esc(sentenceCase(p.gearTitle.replace(/\n/g, ' ').replace(/\.$/, '')))}</a><button type="button" class="rq-text" data-action="types">See all four types</button></div>
        <p class="rq-fine">A playful climbing stereotype, not a psychological diagnosis.${this.checkin ? ' It’s also on its way to your inbox.' : ''} <button type="button" class="rq-text" data-action="restart" style="padding:0">Take it again</button></p>
      </div>${this.posterHTML(p, shared)}</div>`;
    }

    /**
     * The poster slot. Until the card is drawn it shows the illustration over an
     * empty paper band in the card's own 9:16, so nothing jumps when the card lands.
     * Share is the sheet's one primary button, and it sits with the thing it shares.
     */
    posterHTML(p, shared) {
      const ready = this.card?.key === this.cardKey();
      const inner = ready ? this.posterImg(p)
        : `${this.img(this.profileImage(p), 'Illustration of ' + p.name, 'loading="eager"', p.image)}<span class="rq-poster-band" aria-hidden="true"></span><p class="rq-k">Same rocks. Different minds.</p>`;
      const actions = shared ? '' : `<div class="rq-poster-actions">${this.btn('Share the poster →', 'share', true, 'aria-expanded="false"')}${this.btn('Save the poster', 'save', false)}<div class="rq-share-panel" data-rq-share-panel hidden></div></div><p class="rq-fine rq-poster-hint">Or press and hold the poster to save it to your photos.</p>`;
      return `<div class="rq-poster-wrap"><figure class="rq-poster rq-block${ready ? ' rq-poster-card' : ''}" data-rq-poster>${inner}</figure>${actions}</div>`;
    }

    /* ---------- events ---------- */
    onClick(e) {
      // A click anywhere outside the open share panel (and not on Share itself) closes it.
      if (!e.target.closest('[data-rq-share-panel], [data-action="share"]')) this.closeSharePanel();
      const b = e.target.closest('[data-action]'); if (!b || !this.contains(b)) return;
      switch (b.dataset.action) {
        case 'open': this.open(this.result ? 'result' : 'checkin'); break;
        case 'close': this.close(); break;
        case 'back': if (this.position > 0) { this.position--; this.leaveThen(() => this.render('problem')); } else this.leaveThen(() => this.render('checkin')); break;
        case 'next': this.next(); break;
        case 'restart': this.answers = {}; this.position = 0; this.result = null; this.leaveThen(() => this.render(this.checkin ? 'problem' : 'checkin')); break;
        case 'share': if (!this.closeSharePanel(true)) this.share(); break;   // a second click on Share closes the panel
        case 'share-close': this.closeSharePanel(true); break;
        case 'copy-link': this.copyLink(); break;
        case 'save': this.save(); break;
        case 'types': this.toast('Next round: the four types are not built yet.'); break;
      }
    }
    onKey(e) {
      if (this.screen !== 'problem' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target; if (t && (t.tagName === 'INPUT' && t.type !== 'radio' || t.tagName === 'TEXTAREA')) return;
      const k = e.key.toLowerCase();
      const map = { a: 'a', b: 'b', c: 'c', d: 'd', 1: 'a', 2: 'b', 3: 'c', 4: 'd' };
      if (map[k]) { const r = this.stage.querySelector(`[data-rq-answer][value="${map[k]}"]`); if (r) { e.preventDefault(); r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); r.focus(); } }
      else if (e.key === 'Enter' && !e.target.closest('button')) { e.preventDefault(); this.next(); }
    }

    /* ---------- demo: jump to a screen via ?rq-screen= ---------- */
    demoJump(s) {
      const fill = n => { this.data.questions.slice(0, n).forEach((q, i) => { this.answers[q.id] = ['a', 'b', 'c', 'd'][i % 4]; }); };
      if (s === 'checkin') this.open('checkin');
      else if (s.startsWith('problem')) { this.checkin = { email: 'demo@example.com', marketing: false, figure: 'skip' }; const n = parseInt(s.replace('problem', ''), 10) || 1; fill(n - 1); this.position = n - 1; this.open('problem'); }
      else if (s === 'topout') { this.checkin = { email: 'demo@example.com' }; fill(12); this.result = calculate(this.data, this.answers); this.open('topout'); }
      else if (s.startsWith('result')) { this.checkin = { email: 'demo@example.com' }; fill(12); const type = s.split(':')[1]; this.result = calculate(this.data, this.answers); if (this.profile(type)) this.result.id = type; this.open('result'); }
    }
  }
  customElements.define('rungne-climber-quiz', RungneQuiz);
})();
